import { useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { trpc } from "@/lib/trpc";
import { PageChrome } from "@/components/tabs/PageChrome";
import { PlatformNav } from "@/components/platform/PlatformNav";
import {
  Loader2,
  Building2,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  X,
} from "lucide-react";

/**
 * TIER 1 — Platform / SaaS super-admin console. Cross-org, with WRITE
 * controls (org settings, plan/seats/billing, admin grants, ownership
 * transfer, delete-empty-org). Every write is audited via `recordAudit`.
 * The real gate is server-side (`platform.*` → platformAdminProcedure);
 * this page additionally redirects non-platform-admins away so a
 * stray link doesn't dump a wall of FORBIDDEN errors.
 */
export default function PlatformConsole() {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [, navigate] = useLocation();
  const [openOrg, setOpenOrg] = useState<number | null>(null);

  const meQ = trpc.org.me.useQuery(undefined, { staleTime: 60_000 });
  const orgsQ = trpc.platform.orgs.useQuery(undefined, {
    enabled: meQ.data?.platformAdmin === true,
    staleTime: 30_000,
  });
  const dupeQ = trpc.platform.dupeOrgs.useQuery(undefined, {
    enabled: meQ.data?.platformAdmin === true,
    staleTime: 30_000,
  });

  // Not a platform admin → bounce home (server already forbids the
  // data; this just avoids a broken-looking page).
  if (meQ.isSuccess && !meQ.data?.platformAdmin) {
    navigate("/");
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Same chrome as the tabbed screens; bottom tab bar via tabbed(). */}
      <PageChrome backHref="/more" backLabel={isAr ? "العودة" : "Back"}>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-5 w-5 text-coral" />
          <h1 className="font-display text-2xl sm:text-3xl">
            {isAr ? "وحدة تحكّم المنصّة" : "Platform console"}
          </h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          {isAr
            ? "أدوات مشرف المنصّة. الإجراءات الحسّاسة تُسجَّل في سجلّ التدقيق."
            : "Platform-admin controls. Sensitive actions are audited."}
        </p>

        <PlatformNav />

        {/* Duplicate-org health — should stay empty post-fix. */}
        {dupeQ.data && dupeQ.data.length > 0 && (
          <div className="rounded-xl border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 p-4 mb-5 text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              {isAr
                ? "اكتُشِفت مؤسّسات مكرّرة لنفس المالك. يجب ألّا يحدث هذا بعد الإصلاح:"
                : "Duplicate orgs detected for the same owner. This should not happen post-fix:"}{" "}
              {dupeQ.data
                .map(d => `owner ${d.ownerUserId} ×${d.count}`)
                .join(", ")}
            </div>
          </div>
        )}

        {orgsQ.isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {isAr ? "جارٍ التحميل…" : "Loading…"}
          </div>
        )}

        <CreateOrgCard enabled={meQ.data?.platformAdmin === true} />

        {orgsQ.data && (
          <div className="rounded-2xl border bg-card overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-muted/40 border-b text-sm font-medium">
              <Building2 className="h-4 w-4 text-coral" />
              {isAr ? "المؤسّسات" : "Organizations"}
              <span className="ms-auto text-[11px] text-muted-foreground">
                {orgsQ.data.length}
              </span>
            </div>
            <div className="divide-y">
              {orgsQ.data.map(o => {
                const expanded = openOrg === o.id;
                const overSeat =
                  o.seatLimit != null && o.seatUsed > o.seatLimit;
                return (
                  <div key={o.id}>
                    <button
                      type="button"
                      onClick={() => setOpenOrg(expanded ? null : o.id)}
                      className="w-full text-start px-4 py-3 flex items-center gap-3 hover:bg-foreground/[0.03] transition-colors"
                    >
                      {expanded ? (
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">
                          {o.name}{" "}
                          <span className="text-[11px] text-muted-foreground">
                            #{o.id}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {o.ownerName ?? `User ${o.ownerUserId}`}
                          {o.ownerEmail ? ` · ${o.ownerEmail}` : ""}
                        </div>
                      </div>
                      <div className="hidden sm:flex items-center gap-4 text-xs shrink-0">
                        <span className="rounded-full bg-muted px-2 py-0.5">
                          {o.plan}
                        </span>
                        <span
                          className={
                            overSeat
                              ? "text-red-600 font-medium"
                              : "text-muted-foreground"
                          }
                        >
                          {isAr ? "المقاعد" : "seats"} {o.seatUsed}
                          {o.seatLimit != null ? `/${o.seatLimit}` : ""}
                        </span>
                        <span className="text-muted-foreground">
                          {o.memberCount} {isAr ? "أعضاء" : "members"}
                        </span>
                      </div>
                    </button>
                    {expanded && <OrgDetail organizationId={o.id} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <AuditFeed enabled={meQ.data?.platformAdmin === true} />
      </PageChrome>
    </div>
  );
}

function OrgDetail({ organizationId }: { organizationId: number }) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const utils = trpc.useUtils();
  const q = trpc.platform.org.useQuery(
    { organizationId },
    { staleTime: 30_000 },
  );
  const adminsQ = trpc.platform.orgAdmins.useQuery(
    { organizationId },
    { staleTime: 30_000 },
  );

  const refresh = () => {
    utils.platform.orgs.invalidate();
    utils.platform.org.invalidate({ organizationId });
    utils.platform.orgAdmins.invalidate({ organizationId });
  };
  const fail = (e: any) =>
    toast.error(e?.message ?? (isAr ? "فشل" : "Failed"));

  const setSettings = trpc.platform.setOrgSettings.useMutation({
    onSuccess: () => {
      toast.success(isAr ? "تم الحفظ" : "Saved");
      refresh();
    },
    onError: fail,
  });
  const rename = trpc.platform.renameOrg.useMutation({
    onSuccess: () => {
      toast.success(isAr ? "تمت إعادة التسمية" : "Renamed");
      refresh();
    },
    onError: fail,
  });
  const addAdmin = trpc.platform.addOrgAdmin.useMutation({
    onSuccess: () => {
      toast.success(isAr ? "تم منح صلاحية المشرف" : "Org admin granted");
      refresh();
    },
    onError: fail,
  });
  const removeAdmin = trpc.platform.removeOrgAdmin.useMutation({
    onSuccess: () => {
      toast.success(isAr ? "تم سحب الصلاحية" : "Org admin revoked");
      refresh();
    },
    onError: fail,
  });

  // Local editable form state, seeded once the org loads.
  const org = q.data?.org;
  const [name, setName] = useState<string | null>(null);
  const [plan, setPlan] = useState<string | null>(null);
  const [seat, setSeat] = useState<string | null>(null);
  const [billing, setBilling] = useState<string | null>(null);
  const [addUserId, setAddUserId] = useState<string>("");

  if (q.isLoading) {
    return (
      <div className="px-4 py-3 bg-muted/20 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        {isAr ? "جارٍ التحميل…" : "Loading…"}
      </div>
    );
  }
  if (!q.data || !org) return null;
  const { members, tree } = q.data;
  const flat = tree.flat;

  const nameV = name ?? org.name;
  const planV = plan ?? org.plan;
  const seatV = seat ?? (org.seatLimit == null ? "" : String(org.seatLimit));
  const billingV = billing ?? org.billingStatus;
  const adminIds = new Set((adminsQ.data ?? []).map(a => a.id));
  const inputCls =
    "rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-coral";

  return (
    <div className="px-4 py-3 bg-muted/20 space-y-4 text-sm">
      {/* ── Org settings (write) ───────────────────────────────── */}
      <div className="rounded-lg border bg-card p-3">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          {isAr ? "إعدادات المؤسّسة" : "Organization settings"}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] text-muted-foreground">
              {isAr ? "الاسم" : "Name"}
            </span>
            <input
              className={inputCls + " w-48"}
              value={nameV}
              onChange={e => setName(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] text-muted-foreground">
              {isAr ? "الخطّة" : "Plan"}
            </span>
            <select
              className={inputCls}
              value={planV}
              onChange={e => setPlan(e.target.value)}
            >
              <option value="free">free</option>
              <option value="pro">pro</option>
              <option value="enterprise">enterprise</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] text-muted-foreground">
              {isAr ? "حدّ المقاعد" : "Seat limit"}
            </span>
            <input
              className={inputCls + " w-24"}
              type="number"
              min={1}
              placeholder={isAr ? "غير محدود" : "unlimited"}
              value={seatV}
              onChange={e => setSeat(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] text-muted-foreground">
              {isAr ? "الفوترة" : "Billing"}
            </span>
            <select
              className={inputCls}
              value={billingV}
              onChange={e => setBilling(e.target.value)}
            >
              <option value="active">active</option>
              <option value="past_due">past_due</option>
              <option value="canceled">canceled</option>
            </select>
          </label>
          <button
            type="button"
            disabled={setSettings.isPending || rename.isPending}
            onClick={async () => {
              const trimmed = nameV.trim();
              if (trimmed && trimmed !== org.name) {
                await rename.mutateAsync({ organizationId, name: trimmed });
              }
              await setSettings.mutateAsync({
                organizationId,
                plan: planV as "free" | "pro" | "enterprise",
                billingStatus: billingV as
                  | "active"
                  | "past_due"
                  | "canceled",
                seatLimit:
                  seatV.trim() === "" ? null : Math.max(1, Number(seatV)),
              });
            }}
            className="rounded-md bg-coral text-white px-3 py-1.5 text-xs hover:bg-coral/90 disabled:opacity-50"
          >
            {setSettings.isPending || rename.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : isAr ? (
              "حفظ"
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>

      {/* ── Org admins (write) ─────────────────────────────────── */}
      <div className="rounded-lg border bg-card p-3">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          {isAr ? "مشرفو المؤسّسة" : "Organization admins"}
        </div>
        {(adminsQ.data ?? []).length === 0 ? (
          <div className="text-xs text-muted-foreground italic mb-2">
            {isAr ? "لا مشرفين" : "None"}
          </div>
        ) : (
          <ul className="space-y-1 mb-2">
            {(adminsQ.data ?? []).map(a => (
              <li
                key={a.id}
                className="flex items-center gap-2 text-xs"
              >
                <span className="truncate">
                  {a.name ?? `User ${a.id}`}
                  {a.email ? (
                    <span className="text-muted-foreground">
                      {" "}
                      · {a.email}
                    </span>
                  ) : null}
                </span>
                {a.isOwner ? (
                  <span className="text-[10px] rounded-full bg-muted px-1.5 py-0.5 text-muted-foreground">
                    {isAr ? "المالك" : "owner"}
                  </span>
                ) : (
                  <button
                    type="button"
                    title={isAr ? "سحب" : "Revoke"}
                    disabled={removeAdmin.isPending}
                    onClick={() =>
                      removeAdmin.mutate({
                        organizationId,
                        userId: a.id,
                      })
                    }
                    className="text-muted-foreground hover:text-red-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center gap-2">
          <select
            className={inputCls}
            value={addUserId}
            onChange={e => setAddUserId(e.target.value)}
          >
            <option value="">
              {isAr ? "اختر عضواً…" : "Select a member…"}
            </option>
            {members
              .filter(m => !adminIds.has(m.id))
              .map(m => (
                <option key={m.id} value={m.id}>
                  {m.name ?? `User ${m.id}`}
                  {m.email ? ` · ${m.email}` : ""}
                </option>
              ))}
          </select>
          <button
            type="button"
            disabled={!addUserId || addAdmin.isPending}
            onClick={() => {
              addAdmin.mutate({
                organizationId,
                userId: Number(addUserId),
              });
              setAddUserId("");
            }}
            className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-foreground/5 disabled:opacity-50"
          >
            {isAr ? "منح مشرف" : "Grant admin"}
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1.5">
          {isAr
            ? "يجب أن يكون العضو منتمياً لهذه المؤسّسة. المالك لا يُسحب."
            : "Member must belong to this org. The owner can't be revoked."}
        </p>
      </div>

      {/* ── Read-only: members + departments/teams ─────────────── */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
            {isAr ? "الأعضاء" : "Members"} ({members.length})
          </div>
          {members.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">
              {isAr ? "لا أعضاء" : "None"}
            </div>
          ) : (
            <ul className="space-y-1">
              {members.map(m => (
                <li key={m.id} className="text-xs truncate">
                  {m.name ?? `User ${m.id}`}
                  {m.email ? (
                    <span className="text-muted-foreground">
                      {" "}
                      · {m.email}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
            {isAr ? "الأقسام والفِرق" : "Departments & teams"} ({flat.length})
          </div>
          {flat.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">
              {isAr ? "لا فِرق" : "None"}
            </div>
          ) : (
            <ul className="space-y-1">
              {flat.map(t => (
                <li
                  key={t.id}
                  className="text-xs"
                  style={{
                    paddingInlineStart:
                      t.parentTeamId != null ? "14px" : "0px",
                  }}
                >
                  {t.isContainer ? "📁 " : "• "}
                  {t.name}
                  <span className="text-muted-foreground">
                    {" "}
                    ({t.memberCount})
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <DangerZone
        organizationId={organizationId}
        orgName={org.name}
        ownerUserId={org.ownerUserId}
        members={members}
        teamsCount={flat.length}
        refresh={refresh}
      />
    </div>
  );
}

/**
 * Destructive actions (Phase 5): transfer org ownership + delete the
 * org. Both are platform-admin gated server-side too; this UI just
 * guards against the obvious foot-guns (deleting a non-empty org,
 * transferring to a non-member) with disabled buttons + a confirm.
 */
function DangerZone({
  organizationId,
  orgName,
  ownerUserId,
  members,
  teamsCount,
  refresh,
}: {
  organizationId: number;
  orgName: string;
  ownerUserId: number;
  members: Array<{ id: number; name: string | null; email: string | null }>;
  teamsCount: number;
  refresh: () => void;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const utils = trpc.useUtils();
  const [newOwner, setNewOwner] = useState<string>("");
  const transfer = trpc.platform.reassignOrgOwner.useMutation({
    onSuccess: () => {
      toast.success(isAr ? "تم نقل الملكية" : "Ownership transferred");
      setNewOwner("");
      refresh();
    },
    onError: e => {
      const code = e?.message ?? "failed";
      const human: Record<string, [string, string]> = {
        user_not_found: ["المستخدم غير موجود", "User not found"],
        user_not_in_org: [
          "المستخدم ليس عضواً في هذه المؤسّسة",
          "User isn't a member of this organization",
        ],
        user_already_owns_another_org: [
          "المستخدم يمتلك مؤسّسة أخرى بالفعل",
          "User already owns another organization",
        ],
      };
      toast.error(human[code]?.[isAr ? 0 : 1] ?? code);
    },
  });
  const del = trpc.platform.deleteOrg.useMutation({
    onSuccess: () => {
      toast.success(isAr ? "تم حذف المؤسّسة" : "Organization deleted");
      utils.platform.orgs.invalidate();
    },
    onError: e => {
      const code = e?.message ?? "failed";
      const human: Record<string, [string, string]> = {
        org_has_users: [
          "المؤسّسة تضم أعضاء. انقلهم أولاً",
          "Org still has members. Move them out first",
        ],
        org_has_teams: [
          "المؤسّسة تضم فِرقاً. أزلها أولاً",
          "Org still has teams. Remove them first",
        ],
      };
      toast.error(human[code]?.[isAr ? 0 : 1] ?? code);
    },
  });
  const transferCandidates = members.filter(m => m.id !== ownerUserId);
  const canDelete = members.length === 0 && teamsCount === 0;
  const inputCls =
    "rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-coral";
  return (
    <div className="rounded-lg border border-red-200/60 dark:border-red-900/40 bg-red-50/30 dark:bg-red-950/10 p-3 space-y-3">
      <div className="text-xs font-medium uppercase tracking-wide text-red-700 dark:text-red-300 flex items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5" />
        {isAr ? "منطقة خطر" : "Danger zone"}
      </div>

      {/* Transfer ownership */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="text-xs">
          <div className="text-[10px] text-muted-foreground">
            {isAr ? "نقل الملكية" : "Transfer ownership"}
          </div>
          <div className="text-muted-foreground">
            {isAr ? "المالك الحالي:" : "Current owner:"}{" "}
            <span className="text-foreground font-medium">
              {members.find(m => m.id === ownerUserId)?.name ??
                `User ${ownerUserId}`}
            </span>
          </div>
        </div>
        <select
          className={inputCls}
          value={newOwner}
          onChange={e => setNewOwner(e.target.value)}
          disabled={transferCandidates.length === 0}
        >
          <option value="">
            {transferCandidates.length === 0
              ? isAr
                ? "لا يوجد مرشّحون"
                : "no eligible members"
              : isAr
                ? "اختر مالكاً جديداً…"
                : "Select new owner…"}
          </option>
          {transferCandidates.map(m => (
            <option key={m.id} value={m.id}>
              {m.name ?? `User ${m.id}`}
              {m.email ? ` · ${m.email}` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!newOwner || transfer.isPending}
          onClick={() => {
            if (
              !window.confirm(
                isAr
                  ? `نقل ملكية "${orgName}" إلى المستخدم #${newOwner}؟`
                  : `Transfer ownership of "${orgName}" to user #${newOwner}?`,
              )
            )
              return;
            transfer.mutate({
              organizationId,
              newOwnerUserId: Number(newOwner),
            });
          }}
          className="rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 px-2.5 py-1 text-xs disabled:opacity-50"
        >
          {transfer.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : isAr ? (
            "نقل"
          ) : (
            "Transfer"
          )}
        </button>
      </div>

      {/* Delete org */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs flex-1 min-w-0">
          <div className="text-[10px] text-muted-foreground">
            {isAr ? "حذف المؤسّسة" : "Delete organization"}
          </div>
          <div className="text-muted-foreground">
            {canDelete
              ? isAr
                ? "هذه المؤسّسة فارغة وقابلة للحذف."
                : "This organization is empty and can be deleted."
              : isAr
                ? "غير قابلة للحذف: يجب إفراغ الأعضاء والفِرق أولاً."
                : "Not deletable: move members and teams out first."}
          </div>
        </div>
        <button
          type="button"
          disabled={!canDelete || del.isPending}
          onClick={() => {
            if (
              !window.confirm(
                isAr
                  ? `حذف "${orgName}" نهائيّاً؟ لا يمكن التراجع.`
                  : `Delete "${orgName}" permanently? This can't be undone.`,
              )
            )
              return;
            del.mutate({ organizationId });
          }}
          className="rounded-md border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 px-2.5 py-1 text-xs disabled:opacity-50"
        >
          {del.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : isAr ? (
            "حذف"
          ) : (
            "Delete"
          )}
        </button>
      </div>
    </div>
  );
}

/**
 * Cross-org audit feed. Every platform.* + org.* admin action writes
 * a row via `recordAudit`; this surfaces them so you can SEE the
 * trail (who granted org-admin to whom, who changed a plan, etc.).
 */
function AuditFeed({ enabled }: { enabled: boolean }) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const q = trpc.platform.auditLog.useQuery(
    { limit: 100 },
    { enabled, staleTime: 15_000, refetchInterval: 60_000 },
  );
  return (
    <div className="mt-6 rounded-2xl border bg-card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-muted/40 border-b text-sm font-medium">
        <ShieldCheck className="h-4 w-4 text-coral" />
        {isAr ? "سجلّ التدقيق (المنصّة)" : "Audit log (platform-wide)"}
        <span className="ms-auto text-[11px] text-muted-foreground">
          {q.data?.length ?? 0}
        </span>
      </div>
      {q.isLoading && (
        <div className="px-4 py-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          {isAr ? "جارٍ التحميل…" : "Loading…"}
        </div>
      )}
      {q.data && q.data.length === 0 && (
        <div className="px-4 py-6 text-center text-xs text-muted-foreground italic">
          {isAr ? "لا أحداث بعد." : "No events yet."}
        </div>
      )}
      {q.data && q.data.length > 0 && (
        <ul className="divide-y text-sm">
          {q.data.map(r => (
            <AuditRow key={r.id} row={r} isAr={isAr} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One sanitized audit row (Web v1). The SERVER already redacts: masked
 * email, allow-listed metadata only — no raw JSON, no hashes, no user
 * prose. "Technical details" just expands the same redacted key/value set.
 * `break-words` + min-w-0 keep long values wrapping INSIDE the container.
 */
function AuditRow({
  row,
  isAr,
}: {
  row: {
    id: number;
    userId: number;
    action: string;
    entityType: string | null;
    entityId: string | number | null;
    createdAt: unknown;
    actorName: string | null;
    actorEmailMasked: string | null;
    summary: string;
    details: Record<string, string | number | boolean>;
  };
  isAr: boolean;
}) {
  const [open, setOpen] = useState(false);
  const detailEntries = Object.entries(row.details ?? {});
  return (
    <li className="px-4 py-2.5" data-testid="audit-row">
      <div className="flex items-baseline gap-3">
        <code className="font-mono text-[11px] text-coral shrink-0">
          {row.action}
        </code>
        <div className="min-w-0 flex-1 text-xs break-words">
          <span className="text-foreground">
            {row.actorName ?? `User ${row.userId}`}
          </span>
          {row.actorEmailMasked ? (
            <span className="text-muted-foreground"> · {row.actorEmailMasked}</span>
          ) : null}
          {row.entityType ? (
            <span className="text-muted-foreground">
              {" "}
              → {row.entityType}
              {row.entityId ? ` #${row.entityId}` : ""}
            </span>
          ) : null}
          {row.summary ? (
            <span className="text-muted-foreground"> · {row.summary}</span>
          ) : null}
          {detailEntries.length > 0 && (
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              className="ms-2 text-[11px] text-coral hover:underline"
              data-testid="audit-details-toggle"
            >
              {open
                ? isAr
                  ? "إخفاء التفاصيل"
                  : "Hide details"
                : isAr
                  ? "عرض التفاصيل التقنية"
                  : "View technical details"}
            </button>
          )}
        </div>
        <time className="text-[11px] text-muted-foreground shrink-0">
          {relTime(row.createdAt as string | Date, isAr)}
        </time>
      </div>
      {open && detailEntries.length > 0 && (
        <dl
          className="mt-1.5 ms-8 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground break-words"
          data-testid="audit-details"
        >
          {detailEntries.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="font-mono">{k}</dt>
              <dd className="min-w-0 break-words">{String(v)}</dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
}

function relTime(input: string | Date, isAr: boolean): string {
  const d = input instanceof Date ? input : new Date(input);
  const diff = (Date.now() - d.getTime()) / 1000; // seconds
  if (diff < 60) return isAr ? "الآن" : "just now";
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return isAr ? `قبل ${m}د` : `${m}m ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return isAr ? `قبل ${h}س` : `${h}h ago`;
  }
  const days = Math.floor(diff / 86400);
  return isAr ? `قبل ${days}ي` : `${days}d ago`;
}

/**
 * Provision a brand-new organization (Phase 4b — onboarding).
 * Owner must be an existing user (OAuth handles user creation; this
 * just bundles them into a named org). Collapsed by default.
 */
function CreateOrgCard({ enabled }: { enabled: boolean }) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState<"free" | "pro" | "enterprise">("free");
  const [seat, setSeat] = useState("");
  const mut = trpc.platform.createOrg.useMutation({
    onSuccess: () => {
      toast.success(isAr ? "تم إنشاء المؤسّسة" : "Organization created");
      utils.platform.orgs.invalidate();
      setName("");
      setEmail("");
      setSeat("");
      setOpen(false);
    },
    onError: e => {
      const code = e?.message ?? "failed";
      const human: Record<string, [string, string]> = {
        owner_not_found: ["المالك غير موجود", "Owner email not found"],
        owner_already_has_org: [
          "هذا المالك يمتلك مؤسّسة بالفعل",
          "Owner already owns an organization",
        ],
        owner_active_in_other_org: [
          "المالك عضو في مؤسّسة أخرى متعدّدة الأعضاء. انقله إلى مساحة عمل شخصيّة أولاً",
          "Owner is in a multi-user org. Move them to a personal workspace first",
        ],
        name_required: ["الاسم مطلوب", "Name is required"],
      };
      const msg = human[code]?.[isAr ? 0 : 1] ?? code;
      toast.error(msg);
    },
  });
  if (!enabled) return null;
  const inputCls =
    "rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:border-coral";
  return (
    <div className="mb-5 rounded-xl border bg-card">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-start hover:bg-foreground/[0.03] transition-colors"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <Building2 className="h-4 w-4 text-coral" />
        {isAr ? "إنشاء مؤسّسة جديدة" : "Create a new organization"}
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 flex flex-wrap items-end gap-2 border-t">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] text-muted-foreground">
              {isAr ? "الاسم" : "Name"}
            </span>
            <input
              className={inputCls + " w-52"}
              placeholder={isAr ? "اسم المؤسّسة" : "Org name"}
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] text-muted-foreground">
              {isAr ? "بريد المالك" : "Owner email"}
            </span>
            <input
              className={inputCls + " w-64"}
              placeholder="owner@example.com"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] text-muted-foreground">
              {isAr ? "الخطّة" : "Plan"}
            </span>
            <select
              className={inputCls}
              value={plan}
              onChange={e =>
                setPlan(e.target.value as "free" | "pro" | "enterprise")
              }
            >
              <option value="free">free</option>
              <option value="pro">pro</option>
              <option value="enterprise">enterprise</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] text-muted-foreground">
              {isAr ? "حدّ المقاعد" : "Seat limit"}
            </span>
            <input
              className={inputCls + " w-24"}
              type="number"
              min={1}
              placeholder={isAr ? "غير محدود" : "unlimited"}
              value={seat}
              onChange={e => setSeat(e.target.value)}
            />
          </label>
          <button
            type="button"
            disabled={
              mut.isPending || !name.trim() || !email.trim()
            }
            onClick={() =>
              mut.mutate({
                name: name.trim(),
                ownerEmail: email.trim(),
                plan,
                seatLimit:
                  seat.trim() === "" ? null : Math.max(1, Number(seat)),
              })
            }
            className="rounded-md bg-coral text-white px-3 py-1.5 text-xs hover:bg-coral/90 disabled:opacity-50"
          >
            {mut.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : isAr ? (
              "إنشاء"
            ) : (
              "Create"
            )}
          </button>
          <p className="basis-full text-[10px] text-muted-foreground">
            {isAr
              ? "المالك يجب أن يكون مستخدماً موجوداً (سجّل دخوله مرّةً على الأقل). سيُنقَل من مؤسّسته الحاليّة إن كانت مساحة عمل شخصيّة."
              : "Owner must be an existing user (signed in at least once). They'll be moved out of a personal-workspace home org, but never out of a multi-user org."}
          </p>
        </div>
      )}
    </div>
  );
}
